import React from 'react';
import { Grid, Typography } from '@mui/material';
import { makeFields, type FieldKitProps } from './fieldKit';

/** Exemplo no helper do template — não é carregado automaticamente do servidor. */
const DEFAULT_NFSE_SERVICE_DESCRIPTION_TEMPLATE =
  'MENSALIDADE – CESSÃO DE USO DA PLATAFORMA VOVER (SAAS) PARA GESTÃO DE DEMANDAS – COMPETÊNCIA {{competencia}}';

/** Dados fiscais usados pelos DOIS provedores (IPM e NFS-e Nacional). */
const NfseCommonFields: React.FC<FieldKitProps> = props => {
  const { text } = makeFields(props);
  return (
    <>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Dados fiscais comuns
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Usados tanto na emissão pelo IPM quanto pela NFS-e Nacional.
      </Typography>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Serviço / cadastros
      </Typography>
      <Grid container spacing={2}>
        {text('municipalServiceCode', 'Código serviço municipal', 'Opcional — referência interna')}
        {text('municipalServiceName', 'Nome do serviço', 'Descrição auxiliar')}
        {text(
          'nfseIssuerMunicipalRegistration',
          'Inscrição municipal emissor',
          'Emitente (prefeitura)'
        )}
        {text('nfseIssRatePercent', 'Alíquota ISS (%)', 'Ponto ou vírgula decimal')}
        {text('nfseIbgeMunicipioCodigo', 'IBGE município (7 dígitos)', 'Ex.: 4202305')}
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
        Descrição do serviço na NFS-e (discriminação)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Texto enviado como discriminação do serviço (uma cobrança / uma linha típica). Use{' '}
        <code>{'{{competencia}}'}</code> para inserir o mês/ano da cobrança (
        <strong>MM/YYYY</strong>
        ). Opcionais: <code>{'{{billingPeriod}}'}</code>, <code>{'{{mm}}'}</code>,{' '}
        <code>{'{{yyyy}}'}</code>. Se vazio, usa apenas a <strong>descrição da fatura</strong>.
      </Typography>
      <Grid container spacing={2}>
        {text(
          'nfseServiceDescriptionTemplate',
          'Template da discriminação',
          `Ex.: ${DEFAULT_NFSE_SERVICE_DESCRIPTION_TEMPLATE}`,
          { multiline: true, minRows: 3, InputProps: { sx: { fontFamily: 'inherit' } } },
          12
        )}
      </Grid>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 3 }}>
        Reforma Tributária (IBS/CBS) — NBS e classificação
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Tabelas: Nota Nacional (gov.br/nfse), NT 004 e Anexos VII/VIII. Confirme a combinação com o
        contador e valide em homologação.
      </Typography>
      <Grid container spacing={2}>
        {text(
          'nfseNbsCodigo',
          'NBS (Nomenclatura Brasileira de Serviços)',
          '9 dígitos, sem pontos. Obrigatório no IPM (erro 00366) e na NFS-e Nacional (cNBS).',
          { inputProps: { inputMode: 'numeric', maxLength: 9 } },
          6
        )}
        {text(
          'nfseIbsCbsFinNfse',
          'IBS/CBS — Finalidade da NFS-e (finNFSe)',
          '1 dígito; 0 = NFS-e regular (NT 004).',
          { inputProps: { inputMode: 'numeric', maxLength: 1 } },
          6
        )}
        {text(
          'nfseIbsCbsIndFinal',
          'IBS/CBS — Consumidor final (indFinal)',
          '0 = não, 1 = sim (NT 004).',
          { inputProps: { inputMode: 'numeric', maxLength: 1 } },
          6
        )}
        {text(
          'nfseIbsCbsIndOp',
          'IBS/CBS — Indicador de operação (cIndOp)',
          '6 dígitos, Anexo VII da Nota Nacional (ex.: 100301).',
          { inputProps: { inputMode: 'numeric', maxLength: 6 } },
          6
        )}
        {text(
          'nfseIbsCbsCst',
          'IBS/CBS — CST',
          '3 dígitos (ex.: 000 = tributação integral), conforme NT 004.',
          { inputProps: { inputMode: 'numeric', maxLength: 3 } },
          6
        )}
        {text(
          'nfseIbsCbsClassTrib',
          'IBS/CBS — Classificação tributária (cClassTrib)',
          '6 dígitos, Anexo VIII (ex.: 000001). Deve casar com NBS e cIndOp (erro 00368 no IPM).',
          { inputProps: { inputMode: 'numeric', maxLength: 6 } },
          6
        )}
      </Grid>
    </>
  );
};

export default NfseCommonFields;
